import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EndeavorTypeApi } from '../../services/ApiService';

function EndeavorTypeList() {
  const [endeavorTypes, setEndeavorTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadEndeavorTypes();
  }, []);

  const loadEndeavorTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await EndeavorTypeApi.getEndeavorTypes();
      if (!response.ok) {
        throw new Error('Failed to load endeavor types');
      }
      const data = await response.json();
      console.log('EndeavorTypeList: Raw API response:', data);
      setEndeavorTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading endeavor types:', error);
      setError('Failed to load endeavor types. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredEndeavorTypes = endeavorTypes.filter(type =>
    type.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    type.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading endeavor types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-theme-panel" style={{display:'flex',flexDirection:'column',gap:'var(--space-md)'}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{margin:0,fontSize:'1.1rem'}}>Endeavor Types</h2>
        <Link to="/settings/endeavor-types/create">
          <button type="button" className="btn-primary btn-sm">Create New Type</button>
        </Link>
      </div>
      
      {error && (
        <div className="error-message" style={{ 
          color: '#dc3545', 
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px', 
          padding: '0.75rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      <div>
        <input
          type="text"
          placeholder="Search endeavor types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="tp-input"
          style={{maxWidth:'400px',width:'100%'}}
        />
      </div>

      {filteredEndeavorTypes.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem', 
          color: '#666',
          backgroundColor: 'var(--semantic-surface-panel, #f9f9f9)',
          borderRadius: '4px'
        }}>
          {searchTerm ? 'No endeavor types match your search.' : 'No endeavor types found. Create your first one!'}
        </div>
      ) : (
        <div className="settings-theme-table-wrapper"><table className="settings-theme-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEndeavorTypes.map((type) => {
              // FIXED: Use correct field name for ID
              const typeId = type.endeavorTypeId || type.id;
              
              return (
                <tr key={typeId}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {type.icon && (
                        <span style={{ fontSize: '1.2rem' }}>{type.icon}</span>
                      )}
                      <div>
                        <strong>{type.name || 'Unnamed Type'}</strong>
                        {type.color && (
                          <div 
                            style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: type.color,
                              borderRadius: '3px',
                              display: 'inline-block',
                              marginLeft: '0.5rem',
                              border: '1px solid var(--border-subtle)'
                            }}
                            title={`Color: ${type.color}`}
                          />
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{type.description || 'No description'}</td>
                  <td>
                    <span className="settings-theme-badge" style={{borderRadius:'12px'}}>
                      {type.defaultDurationDays || 30} days
                    </span>
                  </td>
                  <td>
                    <span className={"settings-theme-badge" + (type.isActive ? ' active' : '')} style={{borderRadius:'12px'}}>{type.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Link 
                        to={`/settings/endeavor-types/${typeId}/edit`}
                        className="btn-link"
                        onClick={() => {
                          console.log('EndeavorTypeList: Clicking edit link for type:', type);
                          console.log('EndeavorTypeList: Using typeId:', typeId);
                          console.log('EndeavorTypeList: Generated URL:', `/settings/endeavor-types/${typeId}/edit`);
                        }}
                      >
                        Edit
                      </Link>
                      <Link 
                        to={`/settings/endeavor-types/${typeId}/metadata`}
                        className="btn-neutral btn-sm"
                      >
                        Metadata
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })} {/* FIXED: Added missing closing brace and parenthesis */}
          </tbody>
        </table></div>
      )}
      <div className="settings-theme-panel" style={{marginTop:'2rem',padding:'1rem'}}>
        <h4 style={{ margin: '0 0 0.5rem 0' }}>About Endeavor Types</h4>
        <p style={{ margin: '0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          Endeavor types define the different categories of learning activities you can track. Examples include 
          courses, workshops, research projects, or skill development programs. Each type can have its own 
          default duration, color coding, and custom metadata fields.
        </p>
      </div>
    </div>
  );
}

export default EndeavorTypeList;